import React, { useEffect, useState, useCallback } from "react";
import { fetchAccounttypes, fetchOrganizations, fetchCurrencies, fetchUtilAccounts, fetchDetailTypeForAccountType, API_BASE_PATH, client } from "../apiHelper";
import { useToast } from "../hooks/useToast";
import Input from "../components/ui/Input";
import Select from "../components/ui/Select";

import Button from "../components/ui/Button";
import { PlusCircle, Save, Loader2, Info, XCircle } from 'lucide-react';
import { useNavigate } from "react-router-dom";

const NewAccount = () => {
    const { showToast } = useToast();
    const navigate = useNavigate();

    const [user, setUser] = useState(null);
    const [loading, setLoading] = useState(false);
    const [accountTypes, setAccountTypes] = useState([]);
    const [detailTypes, setDetailTypes] = useState([]); // New state for detail types
    const [organizations, setOrganizations] = useState([]);
    const [currencies, setCurrencies] = useState([]);
    const [parentAccounts, setParentAccounts] = useState([]);
    const [formObject, setFormObject] = useState({
        name: "",
        account_code: "",
        accounttype_id: "",
        detailtype_id: "", // New field for detail type
        organization_id: "",
        parent_id: "",
        description: "",
        current_balance: 0.00,
        currency_code: "",
    });
    const [pageError, setPageError] = useState(null);

    // Load Detail Types when accounttype_id changes
    const loadDetailTypes = useCallback(async (accounttype_id) => {
        if (!accounttype_id) {
            setDetailTypes([]);
            setFormObject((prev) => ({ ...prev, detailtype_id: "" }));
            return;
        }

        try {
            const fetchedDetailTypes = await fetchDetailTypeForAccountType(parseInt(accounttype_id));
            const options = fetchedDetailTypes.map((detailType) => ({
                label: detailType.detail_type,
                value: detailType.detailtype_id,
            }));

            setDetailTypes(options);

            setFormObject((prev) => {
                const currentDetailId = prev.detailtype_id;
                const isStillValid = currentDetailId && options.some(opt => opt.value === currentDetailId);
                return {
                    ...prev,
                    detailtype_id: isStillValid ? currentDetailId : ""
                };
            });

        } catch (err) {
            console.error("Error loading detail types:", err);
            const errorMessage = err.response?.data?.detail || "Failed to load detail types.";
            showToast(errorMessage, 'error');
            setPageError(errorMessage);
            setDetailTypes([]);
            setFormObject((prev) => ({ ...prev, detailtype_id: "" }));
        }
    }, [showToast]);

    // Effect to load detail types when accounttype_id changes
    useEffect(() => {
        if (formObject.accounttype_id) {
            loadDetailTypes(formObject.accounttype_id);
        } else {
            setDetailTypes([]);
            setFormObject((prev) => ({ ...prev, detailtype_id: "" }));
        }
    }, [formObject.accounttype_id, loadDetailTypes]);

    // Handle form submission
    const handleSubmit = async (e) => {
        e.preventDefault();
        setLoading(true);
        setPageError(null);

        // Validate detailtype_id if detail types are available
        if (detailTypes.length > 0 && !formObject.detailtype_id) {
            setLoading(false);
            showToast("Please select a detail type.", 'error');
            setPageError("Please select a detail type.");
            return;
        }

        const payload = {
            ...formObject,
            parent_id: formObject.parent_id === "" ? null : formObject.parent_id,
            account_code: formObject.account_code === "" ? null : formObject.account_code,
            detailtype_id: formObject.detailtype_id === "" ? null : parseInt(formObject.detailtype_id), // Include detailtype_id
            current_balance: parseFloat(formObject.current_balance), // Ensure current_balance is a number
        };

        try {
            console.log(payload);
            const response = await client.postData(`${API_BASE_PATH}/accounting/create-account`, payload);
            showToast(response.msg || "Account created successfully!", 'success');
            setFormObject({
                name: "",
                account_code: "",
                accounttype_id: "",
                detailtype_id: "",
                organization_id: "",
                parent_id: "",
                description: "",
                current_balance: 0.00,
                currency_code: "",
            });
            setDetailTypes([]); // Reset detail types
            loadUtilAccounts();
        } catch (err) {
            console.error("Failed to save account information", err);
            const errorMessage = err?.response?.data?.detail || `Failed to save account information: ${err.message || err}`;
            showToast(errorMessage, 'error');
            setPageError(errorMessage);
        } finally {
            setLoading(false);
        }
    };

    // Handle input changes for form fields
    const handleInputChange = useCallback((e) => {
        const { name, value } = e.target;
        setFormObject((prevObj) => ({
            ...prevObj,
            [name]: value,
        }));
    }, []);

    // Load Account Types
    const loadAccountTypes = useCallback(async () => {
        try {
            const fetchedAccountTypes = await fetchAccounttypes();
            if (!Array.isArray(fetchedAccountTypes) || fetchedAccountTypes.length === 0) {
                setAccountTypes([]);
                return;
            }
            const options = fetchedAccountTypes.map((actype) => ({
                label: actype.label,
                value: actype.accounttype_id,
                code: actype.code
            }));
            setAccountTypes(options);
        } catch (err) {
            console.error("Error loading account types:", err);
            const errorMessage = err.response?.data?.detail || "Failed to load account types.";
            showToast(errorMessage, 'error');
            setPageError(errorMessage);
        }
    }, [showToast]);

    // Load Currencies
    const loadCurrencies = useCallback(async () => {
        try {
            const fetchedCurrencies = await fetchCurrencies();
            setCurrencies(fetchedCurrencies.map((currency) => ({
                label: `${currency.setccurr} (${currency.setcnote})`,
                value: currency.setccurr,
            })));
        } catch (err) {
            console.error("Error loading currencies:", err);
            const errorMessage = err.response?.data?.detail || "Failed to load currencies.";
            showToast(errorMessage, 'error');
            setPageError(errorMessage);
        }
    }, [showToast]);

    // Load Organizations
    const loadOrganizations = useCallback(async () => {
        try {
            const fetchedOrganizations = await fetchOrganizations();
            setOrganizations(fetchedOrganizations.map((organization) => ({
                label: organization.organization_name,
                value: organization.organization_id,
            })));
        } catch (err) {
            console.error("Error loading organizations:", err);
            const errorMessage = err.response?.data?.detail || "Failed to load organizations.";
            showToast(errorMessage, 'error');
            setPageError(errorMessage);
        }
    }, [showToast]);

    // Load Utility Accounts (for Parent Account dropdown)
    const loadUtilAccounts = useCallback(async () => {
        if (!user) return;
        try {
            const fetchedUtilAccounts = await fetchUtilAccounts(user);
            setParentAccounts(fetchedUtilAccounts.map(account => ({
                value: account.account_id,
                label: `${account.name} (${account.account_code})`,
            })));
        } catch (err) {
            console.error("Error loading parent accounts:", err);
            const errorMessage = err?.response?.data?.detail || "Failed to load parent accounts.";
            showToast(errorMessage, 'error');
            setPageError(errorMessage);
        }
    }, [user, showToast]);

    // Effect to load user from local storage
    useEffect(() => {
        const lcUser = localStorage.getItem("user");
        if (!lcUser) {
            showToast("Please log in to create accounts.", "info");
            navigate('/login');
        } else {
            setUser(JSON.parse(lcUser));
        }
    }, [showToast, navigate]);

    // Effect to load data when user is available
    useEffect(() => {
        if (user) {
            setLoading(true);
            Promise.all([
                loadAccountTypes(),
                loadOrganizations(),
                loadUtilAccounts(),
                loadCurrencies(),
            ]).finally(() => {
                setLoading(false);
            });
        }
    }, [user, loadAccountTypes, loadOrganizations, loadUtilAccounts, loadCurrencies]);

    // Render loading, error, or main content
    if (loading && !Object.keys(formObject).some(key => formObject[key])) {
        return (
            <div className='flex items-center justify-center min-h-[60vh] text-center p-8 text-text-secondary'>
                <Loader2 size={32} className="animate-spin text-gmail-blue" />
                <p className="ml-3 text-lg">Loading account data...</p>
            </div>
        );
    }

    if (pageError) {
        return (
            <div className='flex flex-col items-center justify-center min-h-[60vh] text-center p-8 text-gmail-red'>
                <XCircle size={32} className="mb-2" />
                <p className="text-lg mb-4">Error: {pageError}</p>
                <Button onClick={() => {
                    if (user) {
                        loadAccountTypes();
                        loadOrganizations();
                        loadUtilAccounts();
                        loadCurrencies();
                        if (formObject.accounttype_id) loadDetailTypes(formObject.accounttype_id);
                    }
                }} variant="primary">
                    Retry Loading Data
                </Button>
            </div>
        );
    }

    if (!user) {
        return (
            <div className='flex flex-col items-center justify-center min-h-[60vh] text-center p-8 text-orange-600'>
                <Info size={32} className="mb-2" />
                <p className="text-lg mb-4">Please log in to create new accounts.</p>
            </div>
        );
    }

    return (
        <div className="flex flex-1 justify-center items-start py-10 px-4 sm:px-6 lg:px-8 bg-gray-100 min-h-screen">
            <div className="w-full max-w-2xl bg-white rounded-xl shadow-lg p-8">
                <h2 className="text-3xl font-bold text-text-primary mb-8 text-center flex items-center justify-center gap-3">
                    <PlusCircle size={32} className="text-gmail-blue" /> Create New Account
                </h2>
                <p className="text-text-secondary text-center mb-8 max-w-md mx-auto">
                    Define a new account to categorize your financial transactions within the chart of accounts.
                </p>

                <form onSubmit={handleSubmit} className="grid grid-cols-1 md:grid-cols-2 gap-x-8 gap-y-6">
                    {/* Account Name */}
                    <Input
                        name="name"
                        value={formObject?.name || ""}
                        onChange={handleInputChange}
                        label="Account Name"
                        type="text"
                        id="name"
                        placeholder="e.g., Cash, Accounts Receivable, Sales Revenue"
                        required
                        className="md:col-span-2"
                    />

                    {/* Account Code (Optional) */}
                    <Input
                        name="account_code"
                        value={formObject?.account_code || ""}
                        onChange={handleInputChange}
                        label="Account Code (Optional)"
                        type="text"
                        id="account_code"
                        placeholder="e.g., 1000, 2010"
                    />

                    {/* Currency Code */}
                    <Select
                        name="currency_code"
                        value={formObject?.currency_code || ""}
                        onChange={handleInputChange}
                        label="Currency Code"
                        options={currencies}
                        placeholder="Select a currency"
                        required
                    />

                    {/* Account Class (Account Type) */}
                    <Select
                        name="accounttype_id"
                        value={formObject?.accounttype_id || ""}
                        onChange={handleInputChange}
                        label="Account Class"
                        options={accountTypes}
                        placeholder="Select an account class"
                        required
                    />

                    {/* Detail Type */}
                    {detailTypes.length > 0 && (
                        <Select
                            name="detailtype_id"
                            value={formObject?.detailtype_id || ""}
                            onChange={handleInputChange}
                            label="Detail Type"
                            options={detailTypes}
                            placeholder="Select a detail type"
                            required
                        />
                    )}

                    {/* Opening Balance */}
                    <Input
                        name="current_balance"
                        value={formObject?.current_balance || ""}
                        onChange={handleInputChange}
                        label="Opening Balance"
                        type="number"
                        id="current_balance"
                        placeholder="Enter opening balance"
                        steps="0.01"
                        className="md:col-span-2"
                    />

                    {/* Owner Organization */}
                    <Select
                        name="organization_id"
                        value={formObject?.organization_id || ""}
                        onChange={handleInputChange}
                        label="Owner Organization"
                        options={organizations}
                        placeholder="Select an organization"
                        required
                    />

                    {/* Parent Account */}
                    <Select
                        name="parent_id"
                        value={formObject?.parent_id || ""}
                        onChange={handleInputChange}
                        label="Parent Account (Optional)"
                        options={parentAccounts}
                        placeholder="Select a parent account (if applicable)"
                    />

                    {/* Description */}
                    <Input
                        name="description"
                        value={formObject?.description || ""}
                        onChange={handleInputChange}
                        label="Brief Description (Optional)"
                        type="textarea"
                        id="description"
                        placeholder="Provide a brief description for this account's purpose"
                        className="md:col-span-2"
                    />

                    {/* Submit Button */}
                    <div className="md:col-span-2 flex justify-end mt-6">
                        <Button
                            type="submit"
                            variant="primary"
                            className="flex items-center gap-2 px-6 py-3 text-lg"
                            disabled={loading}
                        >
                            {loading ? (
                                <Loader2 className="animate-spin h-5 w-5" />
                            ) : (
                                <Save size={20} />
                            )}
                            {loading ? 'Saving Account...' : 'Save Account Information'}
                        </Button>
                    </div>
                </form>
            </div>
        </div>
    );
};

export default NewAccount;